import { printHouseProfile } from "@/store/print-house-profile";
import SearchIcon from "@mui/icons-material/Search";
import React, { useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  CountryIso2,
  defaultCountries,
  FlagImage,
  ParsedCountry,
  parseCountry,
  usePhoneInput,
} from "react-international-phone";
import "react-international-phone/style.css";
import { useRecoilValue } from "recoil";
import { createGlobalStyle } from "styled-components";
import { usePrintHousePreferences } from "@/hooks/use-print-house-preferences";
import { EPreferences } from "@/enums/prefernces";
import { FONT_FAMILY } from "@/utils/font-family";

interface IProps {
  onChange: (value: string) => void;
  value: string;
  autoFocus?: boolean;
  customStyle?: React.CSSProperties; // Add the style property
  defaultCountry?: string;
  variant?: "default" | "circular";
  placeholder?: string;
  labelText?: string;
}

const PhoneInputComponent = ({
  onChange,
  value,
  autoFocus,
  customStyle,
  defaultCountry,
  variant = "default",
  placeholder = "",
  labelText = "",
}: IProps) => {
  const printHouseProfileState = useRecoilValue<any>(printHouseProfile);
  const { CheckPreferenceValue } = usePrintHousePreferences();
  const { t } = useTranslation();

  const handleKeyPress = (event) => {
    if (event.key === "Enter") {
      event.preventDefault();
    }
  };

  const inputHeight = customStyle?.height
    ? `${
        typeof customStyle.height === "number"
          ? customStyle.height + "px"
          : customStyle.height
      } !important`
    : "var(--theme-input-height, 40px) !important";

  const GlobalStyleCircular = createGlobalStyle`
    .react-international-phone-input-container {
      direction: ltr;
      display: flex;
      flex-direction: row;
    }
    .react-international-phone-input-container .react-international-phone-input {
      height: ${inputHeight};
      width: 100%;
      border-radius: 0px var(--theme-radius-md, 8px) var(--theme-radius-md, 8px) 0px;
      border: 1px solid var(--theme-input-border-color, none) !important;
      box-shadow: var(--theme-input-shadow, 0px 1px 2px 0px rgba(16, 24, 40, 0.05));
      text-align: left;
      direction: ltr;
      background-color: var(--theme-bg-primary, #FFF);
    }
    .react-international-phone-input-container .react-international-phone-country-selector-button {
      height: ${inputHeight};
      border-radius: var(--theme-radius-md, 8px) 0px 0px var(--theme-radius-md, 8px);
      border: 1px solid var(--theme-input-border-color, none) !important;
      box-shadow: var(--theme-input-shadow, 0px 1px 2px 0px rgba(4, 24, 65, 0.05));
      background-color: var(--theme-bg-primary, #FFF);
    }

    .react-international-phone-input::placeholder {
     color: var(--theme-input-placeholder-color, #B1B1B1) !important;
  opacity: 1;
  font-family: var(--theme-font-family-base, 'Heebo'), sans-serif;
  font-style: normal;
  font-weight: var(--theme-font-weight-medium, 500);
  }
  `;

  const GlobalStyle = createGlobalStyle`
.react-international-phone-input-container {
  direction: ltr;
  display: flex;
  flex-direction: row;
}
.react-international-phone-input-container .react-international-phone-input {
  height: ${inputHeight};
  border: 1px solid var(--theme-input-border-color, none) !important;
  width: 100%;
  box-shadow: var(--theme-input-shadow, 0px 1px 10px rgba(0, 0, 0, 0.08)) !important;
  -webkit-appearance: none;
  text-align: left;
  direction: ltr;
  border-radius: 0px var(--theme-radius-md, 8px) var(--theme-radius-md, 8px) 0px;
  background-color: var(--theme-bg-primary, #FFF);
}
.react-international-phone-input-container
  .react-international-phone-country-selector-button {
  height: ${inputHeight};
  border: 1px solid var(--theme-input-border-color, none) !important;
  box-shadow: var(--theme-input-shadow, 0px 1px 10px rgba(0, 0, 0, 0.08)) !important;
  border-radius: var(--theme-radius-md, 8px) 0px 0px var(--theme-radius-md, 8px);
  background-color: var(--theme-bg-primary, #FFF);
}

.react-international-phone-input::placeholder {
 color: var(--theme-input-placeholder-color, #B1B1B1) !important;
  opacity: 1;
  font-family: var(--theme-font-family-base, 'Heebo'), sans-serif;
  font-style: normal;
  font-weight: var(--theme-font-weight-medium, 500);
  }
`;
  const showPrefix = CheckPreferenceValue(EPreferences.SHOW_PHONE_PREFIX);
  const resolvedCountry = (
    defaultCountry || printHouseProfileState?.country || "us"
  )
    .toString()
    .toLowerCase() as CountryIso2;
  const inputRef = useRef<HTMLInputElement | null>(null);
  const selectorRef = useRef<HTMLDivElement | null>(null);
  const searchRef = useRef<HTMLInputElement | null>(null);
  const listRef = useRef<HTMLUListElement | null>(null);
  const [menuOpen, setMenuOpen] = useState(false);
  const [countryQuery, setCountryQuery] = useState("");
  const [activeIndex, setActiveIndex] = useState(0);

  const { inputValue, handlePhoneValueChange, country, setCountry } =
    usePhoneInput({
      defaultCountry: resolvedCountry,
      value: value || "",
      disableDialCodeAndPrefix: !showPrefix,
      disableFormatting: !showPrefix,
      inputRef,
      onChange: (data) => {
        if (data.phone.length !== 4) onChange(data.phone);
      },
    });

  const countries = useMemo(
    () => defaultCountries.map((item) => parseCountry(item)),
    [],
  );

  const filteredCountries = useMemo(() => {
    const query = countryQuery.trim().toLowerCase().replace(/^\+/, "");
    if (!query) return countries;
    return countries.filter((item) => {
      const name = item.name.toLowerCase();
      const iso = item.iso2.toLowerCase();
      return (
        name.includes(query) ||
        iso.startsWith(query) ||
        item.dialCode.startsWith(query)
      );
    });
  }, [countries, countryQuery]);

  const closeMenu = () => {
    setMenuOpen(false);
    setCountryQuery("");
  };

  const openMenu = () => {
    if (!showPrefix) return;
    const selectedIndex = countries.findIndex(
      (item) => item.iso2 === country?.iso2,
    );
    setActiveIndex(selectedIndex >= 0 ? selectedIndex : 0);
    setCountryQuery("");
    setMenuOpen(true);
  };

  const selectCountry = (next: ParsedCountry) => {
    setCountry(next.iso2, { focusOnInput: true });
    closeMenu();
  };

  useEffect(() => {
    if (!menuOpen) return;
    const timer = window.setTimeout(() => searchRef.current?.focus(), 0);
    return () => window.clearTimeout(timer);
  }, [menuOpen]);

  useEffect(() => {
    if (!menuOpen) return;
    const onPointerDown = (event: MouseEvent) => {
      if (!selectorRef.current?.contains(event.target as Node)) {
        closeMenu();
      }
    };
    document.addEventListener("mousedown", onPointerDown);
    return () => document.removeEventListener("mousedown", onPointerDown);
  }, [menuOpen]);

  useEffect(() => {
    if (!menuOpen || !listRef.current) return;
    const option = listRef.current.querySelector<HTMLElement>(
      `[data-index="${activeIndex}"]`,
    );
    if (!option) return;
    const top = option.offsetTop;
    const bottom = top + option.offsetHeight;
    const viewTop = listRef.current.scrollTop;
    const viewBottom = viewTop + listRef.current.clientHeight;
    if (top < viewTop) {
      listRef.current.scrollTop = top;
    } else if (bottom > viewBottom) {
      listRef.current.scrollTop = bottom - listRef.current.clientHeight;
    }
  }, [activeIndex, menuOpen, filteredCountries]);

  const onSearchKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    event.stopPropagation();
    if (event.key === "ArrowDown") {
      event.preventDefault();
      setActiveIndex((index) =>
        Math.min(index + 1, Math.max(filteredCountries.length - 1, 0)),
      );
      return;
    }
    if (event.key === "ArrowUp") {
      event.preventDefault();
      setActiveIndex((index) => Math.max(index - 1, 0));
      return;
    }
    if (event.key === "Enter") {
      event.preventDefault();
      const next = filteredCountries[activeIndex];
      if (next) selectCountry(next);
      return;
    }
    if (event.key === "Escape") {
      event.preventDefault();
      closeMenu();
      inputRef.current?.focus();
    }
  };

  return (
    <div
      onKeyPress={handleKeyPress}
      dir="ltr"
      className="gm-phone-input"
    >
      {variant === "circular" ? <GlobalStyleCircular /> : <GlobalStyle />}
      {labelText && (
        <div style={{ ...FONT_FAMILY.Lexend(600, 14) }}>{labelText}</div>
      )}
      <div
        className="react-international-phone-input-container"
        style={{ minWidth: 150, ...customStyle }}
      >
        <div
          className="react-international-phone-country-selector"
          ref={selectorRef}
        >
          <button
            type="button"
            className={[
              "react-international-phone-country-selector-button",
              menuOpen
                ? "react-international-phone-country-selector-button--active"
                : "",
              !showPrefix
                ? "react-international-phone-country-selector-button--hide-dropdown"
                : "",
            ]
              .filter(Boolean)
              .join(" ")}
            data-country={country?.iso2}
            title={country?.name}
            aria-label={country?.name || t("header.search")}
            aria-haspopup="listbox"
            aria-expanded={menuOpen}
            disabled={!showPrefix}
            onMouseDown={(event) => event.preventDefault()}
            onClick={() => (menuOpen ? closeMenu() : openMenu())}
          >
            <div className="react-international-phone-country-selector-button__button-content">
              <FlagImage
                iso2={country?.iso2}
                className="react-international-phone-country-selector-button__flag-emoji"
              />
              {showPrefix && (
                <div
                  className={[
                    "react-international-phone-country-selector-button__dropdown-arrow",
                    menuOpen
                      ? "react-international-phone-country-selector-button__dropdown-arrow--active"
                      : "",
                  ]
                    .filter(Boolean)
                    .join(" ")}
                />
              )}
            </div>
          </button>
          {showPrefix && menuOpen && (
            <div
              className="react-international-phone-country-selector-dropdown gm-phone-country-menu"
              dir="ltr"
            >
              <div className="gm-phone-country-search-row">
                <label className="gm-phone-country-search">
                  <SearchIcon className="gm-phone-country-search-icon" />
                  <input
                    ref={searchRef}
                    type="text"
                    value={countryQuery}
                    placeholder={t("header.search")}
                    aria-label={t("header.search")}
                    autoComplete="off"
                    onChange={(event) => {
                      setCountryQuery(event.target.value);
                      setActiveIndex(0);
                    }}
                    onKeyDown={onSearchKeyDown}
                  />
                </label>
              </div>
              <ul
                ref={listRef}
                className="gm-phone-country-list"
                role="listbox"
                aria-label={t("header.search")}
              >
                {filteredCountries.map((item, index) => {
                  const selected = item.iso2 === country?.iso2;
                  const focused = index === activeIndex;
                  return (
                    <li
                      key={item.iso2}
                      data-index={index}
                      data-country={item.iso2}
                      role="option"
                      aria-selected={selected}
                      title={item.name}
                      className={[
                        "react-international-phone-country-selector-dropdown__list-item",
                        selected
                          ? "react-international-phone-country-selector-dropdown__list-item--selected"
                          : "",
                        focused
                          ? "react-international-phone-country-selector-dropdown__list-item--focused"
                          : "",
                      ]
                        .filter(Boolean)
                        .join(" ")}
                      onMouseEnter={() => setActiveIndex(index)}
                      onMouseDown={(event) => {
                        event.preventDefault();
                        selectCountry(item);
                      }}
                    >
                      <span className="react-international-phone-country-selector-dropdown__list-item-country-name">
                        {item.name}
                      </span>
                      <span
                        className="react-international-phone-country-selector-dropdown__list-item-dial-code"
                        dir="ltr"
                      >
                        +{item.dialCode}
                      </span>
                    </li>
                  );
                })}
              </ul>
            </div>
          )}
        </div>
        <input
          ref={inputRef}
          className="react-international-phone-input"
          type="tel"
          dir="ltr"
          value={inputValue}
          placeholder={placeholder}
          autoFocus={autoFocus}
          onChange={handlePhoneValueChange}
        />
      </div>
    </div>
  );
};

export { PhoneInputComponent };
